import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

function authHeaders() {
  const token = localStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const apiMsg = (e) =>
  e?.response?.data?.message ||
  e?.response?.data?.error ||
  (typeof e?.response?.data === "string" ? e.response.data : null) ||
  e?.message ||
  "Request failed";

const toISODate = (d) => {
  if (!d) return undefined;
  if (typeof d === "string") return d.slice(0, 10);
  try { return new Date(d).toISOString().slice(0, 10); } catch { return undefined; }
};

const toNumberOrNull = (v) => {
  if (v === "" || v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function client(path) {
  return axios.create({
    baseURL: `${BASE_URL}${path}`,
    headers: { "Content-Type": "application/json", ...authHeaders() },
  });
}

function normalizePlan(payload = {}) {
  return {
    batchId: toNumberOrNull(payload.batchId),
    animalId: toNumberOrNull(payload.animalId || payload.livestockId),
    livestockId: toNumberOrNull(payload.livestockId || payload.animalId),
    diseaseCaseId: toNumberOrNull(payload.diseaseCaseId),
    medicineName: String(payload.medicineName || "").trim(),
    inventoryId: toNumberOrNull(payload.inventoryId),
    route: payload.route || "oral",
    doseQuantity: toNumberOrNull(payload.doseQuantity || payload.doseAmount),
    doseAmount: toNumberOrNull(payload.doseAmount || payload.doseQuantity),
    doseUnit: payload.doseUnit || "ml",
    frequencyPerDay: toNumberOrNull(payload.frequencyPerDay) || 1,
    startDate: toISODate(payload.startDate),
    endDate: toISODate(payload.endDate),
    active: payload.active !== false,
    notes: String(payload.notes || "").trim(),
  };
}

export async function listBatchesIdName() {
  const res = await axios.get(`${BASE_URL}/api/public/batches/all`, { headers: authHeaders() });
  return Array.isArray(res.data) ? res.data : [];
}

export const listBatches = listBatchesIdName;

export async function listLivestockByBatch(batchId) {
  if (!batchId) return [];
  const res = await axios.get(`${BASE_URL}/api/public/livestock/batch/${batchId}`, { headers: authHeaders() });
  return Array.isArray(res.data) ? res.data : [];
}

export async function listMedicinePlans(batchId, diseaseCaseId) {
  const { data } = await client("/api/medicine-plans").get("", {
    params: { batchId: batchId || undefined, diseaseCaseId: diseaseCaseId || undefined },
  });
  return Array.isArray(data) ? data : [];
}

export async function createMedicinePlan(payload) {
  const { data } = await client("/api/medicine-plans").post("", normalizePlan(payload));
  return data;
}

export async function updateMedicinePlan(id, payload) {
  const { data } = await client("/api/medicine-plans").put(`/${id}`, normalizePlan(payload));
  return data;
}

export async function deleteMedicinePlan(id) {
  const { data } = await client("/api/medicine-plans").patch(`/${id}/stop`);
  return data;
}

export async function stopMedicinePlan(id) {
  return deleteMedicinePlan(id);
}

export async function listMedicineTasks({ batchId, diseaseCaseId, date, from, to, status } = {}) {
  const { data } = await client("/api/medicine-tasks").get("", {
    params: { batchId, diseaseCaseId, date: toISODate(date), from: toISODate(from), to: toISODate(to), status: status || undefined },
  });
  return Array.isArray(data) ? data : [];
}

export async function decideMedicineTask(taskId, payload = {}) {
  const status = String(payload.status || "").toUpperCase();
  if (status === "SKIPPED" || status === "SKIP") {
    const { data } = await client("/api/medicine-tasks").patch(`/${taskId}/skip`, { notes: payload.notes || "" });
    return data;
  }
  if (status === "NOT_GIVEN") {
    const { data } = await client("/api/medicine-tasks").patch(`/${taskId}/not-given`, { notes: payload.notes || "" });
    return data;
  }
  if (status === "RESCHEDULED") {
    const { data } = await client("/api/medicine-tasks").patch(`/${taskId}/reschedule`, {
      rescheduledToDate: toISODate(payload.rescheduledToDate), notes: payload.notes || "",
    });
    return data;
  }
  const { data } = await client("/api/medicine-tasks").patch(`/${taskId}/given`, {
    actualQuantity: toNumberOrNull(payload.actualQuantity), conditionToday: payload.conditionToday,
    actionDate: toISODate(payload.actionDate), notes: payload.notes || "",
  });
  return data;
}

// Kept only for old imports. Manual generation is no longer needed.
export async function generateMedicineTasks() { return []; }
export async function listMedicineLogs() { return []; }
export async function createMedicineLog() { throw new Error("Use createMedicinePlan instead."); }
export async function updateMedicineLog() { throw new Error("Use updateMedicinePlan instead."); }
export async function deleteMedicineLog() { throw new Error("Use stopMedicinePlan instead."); }
export async function setMedicineGivenToday() { throw new Error("Use decideMedicineTask instead."); }

const medicineService = {
  listBatchesIdName,
  listBatches,
  listLivestockByBatch,
  listMedicinePlans,
  createMedicinePlan,
  updateMedicinePlan,
  deleteMedicinePlan,
  stopMedicinePlan,
  listMedicineTasks,
  decideMedicineTask,
  generateMedicineTasks,
  listMedicineLogs,
  createMedicineLog,
  updateMedicineLog,
  deleteMedicineLog,
  setMedicineGivenToday,
  _apiMsg: apiMsg,
};

export default medicineService;
